import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SavedPointsProvider } from './contexts/SavedPointsContext';
import LayoutWrapper from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import Login from './pages/Login';

// Pages are lazy-loaded so each becomes its own chunk and stays out of the
// initial bundle. Login, the layout and the route guards are kept eager since
// they are small and needed on the very first paint.
const ProcurementDashboard = lazy(() => import('./pages/ProcurementDashboard'));
const TransportersDashboard = lazy(() => import('./pages/TransportersDashboard'));
const UserList = lazy(() => import('./pages/Users/UserList'));
const CreateUser = lazy(() => import('./pages/Users/CreateUser'));
const EditUser = lazy(() => import('./pages/Users/EditUser'));
const Items = lazy(() => import('./pages/Items'));
const TransporterList = lazy(() => import('./pages/TransporterBidding/Transporters/TransporterList'));
const CreateTransporter = lazy(() => import('./pages/TransporterBidding/Transporters/CreateTransporter'));
const EditTransporter = lazy(() => import('./pages/TransporterBidding/Transporters/EditTransporter'));
const TransporterHistory = lazy(() => import('./pages/TransporterBidding/Transporters/TransporterHistory'));
const BidList = lazy(() => import('./pages/TransporterBidding/TransporterBids/T_BidList'));
const CreateBid = lazy(() => import('./pages/TransporterBidding/TransporterBids/T_CreateBid'));
const BidDetails = lazy(() => import('./pages/TransporterBidding/TransporterBids/T_BidDetails'));
const MergeWorkspace = lazy(() => import('./pages/TransporterBidding/TransporterBids/T_MergeWorkspace'));
const MergeOpportunities = lazy(() => import('./pages/TransporterBidding/TransporterBids/T_MergeOpportunities'));
const DealList = lazy(() => import('./pages/TransporterBidding/TransporterDeals/T_DealList'));
const LegacyTrackingRedirect = lazy(() => import('./pages/TransporterBidding/TransporterTracking/LegacyTrackingRedirect'));
const VendorList = lazy(() => import('./pages/ProcurementBidding/Vendors/VendorList'));
const CreateVendor = lazy(() => import('./pages/ProcurementBidding/Vendors/CreateVendor'));
const EditVendor = lazy(() => import('./pages/ProcurementBidding/Vendors/EditVendor'));
const ProcurementBidList = lazy(() => import('./pages/ProcurementBidding/ProcurementBids/P_BidList'));
const CreateProcurementBid = lazy(() => import('./pages/ProcurementBidding/ProcurementBids/P_CreateBid'));
const ProcurementBidDetails = lazy(() => import('./pages/ProcurementBidding/ProcurementBids/P_BidDetails'));
const ProcurementDealList = lazy(() => import('./pages/ProcurementBidding/ProcurementDeals/P_DealList'));
const Dispatch = lazy(() => import('./pages/ProcurementBidding/Dispatch'));
const Onboarding = lazy(() => import('./pages/ProcurementBidding/Onboarding'));
const SavedPoints = lazy(() => import('./pages/SavedPoints'));

const GlobalSpinner = () => (
  <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
    <Spin size="large" />
  </div>
);

// Fallback shown inside the layout while a lazy page chunk loads. Uses a
// min-height instead of full-viewport so the layout chrome stays put.
const PageSpinner = () => (
  <div style={{ display: 'flex', minHeight: '60vh', justifyContent: 'center', alignItems: 'center' }}>
    <Spin size="large" />
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <LayoutWrapper>
            <Suspense fallback={<PageSpinner />}>
              <Routes>
                <Route index element={<RoleBasedRedirect />} />
                <Route path="procurement" element={<ProtectedRoute element={<ProcurementDashboard />} allowedRoles={['admin', 'manager', 'manager_procurement']} />} />
                <Route path="transporter" element={<ProtectedRoute element={<TransportersDashboard />} allowedRoles={['admin']} />} />
                <Route path="dashboard" element={<ProtectedRoute element={<ProcurementDashboard />} allowedRoles={['admin','manager','manager_procurement']} />} />
                <Route path="users" element={<ProtectedRoute element={<UserList />} allowedRoles={['admin']} />} />
                <Route path="users/create" element={<ProtectedRoute element={<CreateUser />} allowedRoles={['admin']} />} />
                <Route path="users/edit/:id" element={<ProtectedRoute element={<EditUser />} allowedRoles={['admin']} />} />
                <Route path="transporters" element={<ProtectedRoute element={<TransporterList />} allowedRoles={['admin','manager']} />} />
                <Route path="transporters/create" element={<ProtectedRoute element={<CreateTransporter />} allowedRoles={['admin','manager']} />} />
                <Route path="transporters/edit/:id" element={<ProtectedRoute element={<EditTransporter />} allowedRoles={['admin','manager']} />} />
                <Route path="transporters/history" element={<ProtectedRoute element={<TransporterHistory />} allowedRoles={['admin','manager']} />} />
                <Route path="bids" element={<ProtectedRoute element={<BidList />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="bids/create" element={<ProtectedRoute element={<CreateBid />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="bids/merge" element={<ProtectedRoute element={<MergeWorkspace />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="bids/merge-opportunities" element={<ProtectedRoute element={<MergeOpportunities />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="bids/:id" element={<ProtectedRoute element={<BidDetails />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="deals" element={<ProtectedRoute element={<DealList />} allowedRoles={['admin','manager','staff_transport']} />} />
                {/* Order tracking moved onto the bid page; old links go there. */}
                <Route path="tracking" element={<ProtectedRoute element={<LegacyTrackingRedirect />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="tracking/:id" element={<ProtectedRoute element={<LegacyTrackingRedirect />} allowedRoles={['admin','manager','staff_transport']} />} />
                <Route path="items" element={<ProtectedRoute element={<Items />} allowedRoles={['admin','manager','manager_procurement', 'staff_transport','staff_procurement']} />} />
                <Route path="vendors" element={<ProtectedRoute element={<VendorList />} allowedRoles={['admin','manager_procurement']} />} />
                <Route path="vendors/create" element={<ProtectedRoute element={<CreateVendor />} allowedRoles={['admin','manager_procurement']} />} />
                <Route path="vendors/edit/:id" element={<ProtectedRoute element={<EditVendor />} allowedRoles={['admin','manager_procurement']} />} />
                <Route path="procurement-bids" element={<ProtectedRoute element={<ProcurementBidList />} allowedRoles={['admin','manager_procurement','staff_procurement']} />} />
                <Route path="procurement-bids/create" element={<ProtectedRoute element={<CreateProcurementBid />} allowedRoles={['admin','manager_procurement','staff_procurement']} />} />
                <Route path="procurement-bids/:id" element={<ProtectedRoute element={<ProcurementBidDetails />} allowedRoles={['admin','manager_procurement','staff_procurement']} />} />
                <Route path="procurement-deals" element={<ProtectedRoute element={<ProcurementDealList />} allowedRoles={['admin','manager_procurement']} />} />
                <Route path="dispatch" element={<ProtectedRoute element={<Dispatch />} allowedRoles={['admin','manager_procurement','staff_procurement']} />} />
                <Route path="onboarding" element={<ProtectedRoute element={<Onboarding />} allowedRoles={['admin','manager_procurement']} />} />
                <Route path="saved-points" element={<ProtectedRoute element={<SavedPoints />} allowedRoles={['admin','manager','manager_procurement','staff_transport','staff_procurement']} />} />

                <Route path="*" element={<RoleBasedRedirect />} />
              </Routes>
            </Suspense>
          </LayoutWrapper>
        }
      />
    </Routes>
  );
}

function App() {
  const { loading } = useAuth();
  if (loading) return <GlobalSpinner />;
  return (
    <Router>
      <SavedPointsProvider>
        <AppRoutes />
      </SavedPointsProvider>
    </Router>
  );
}

export default function Root() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
